'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import GXGLayout from '@/components/GXGLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const CoinIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="#f59e0b"><circle cx="12" cy="12" r="10"/></svg>;

const staticBadges = [
  { name: 'Speed Runner', icon: '⏱️', color: 'from-blue-600 to-cyan-600' },
  { name: 'Boss Slayer', icon: '💀', color: 'from-red-600 to-rose-600' },
  { name: 'Treasure Hunter', icon: '🏆', color: 'from-yellow-600 to-amber-600' },
  { name: 'Retro Master', icon: '🎮', color: 'from-purple-600 to-indigo-600' },
  { name: 'Event Veteran', icon: '🎪', color: 'from-green-600 to-teal-600' },
  { name: 'Community Hero', icon: '🤝', color: 'from-pink-600 to-rose-600' },
];

const TABS = ['Overview', 'Missions', 'Proof Submissions', 'Coins', 'Rewards', 'Bookings', 'Trade Requests', 'Game Requests', 'Activity'] as const;
type Tab = typeof TABS[number];

interface PassportEntry {
  id: string;
  game_title: string | null;
  played_at: string;
  session_notes: string | null;
  rating: number | null;
}

interface ActivityEntry {
  id: string;
  action: string;
  entity_type: string | null;
  details: any;
  created_at: string;
}

interface MissionSubmission {
  id: string;
  mission_id: string | null;
  status: string;
  player_notes: string | null;
  notes: string | null;
  admin_notes: string | null;
  proof_count_submitted: number;
  proof_count_required: number;
  reward_coins: number;
  coins_awarded: boolean;
  xp_awarded: number;
  rewards_awarded: boolean;
  proof_links: string[] | null;
  proof_files: string[] | null;
  created_at: string;
  updated_at: string;
  missions?: { title: string | null; difficulty: string | null; xp_reward?: number | null; coin_reward?: number | null; reward_coins?: number | null };
  proofs?: { id: string; file_url: string | null; file_type: string | null; file_name: string | null; external_url: string | null; proof_number: number }[];
}

interface CoinTransaction {
  id: string;
  amount: number;
  type: string;
  source: string;
  description: string | null;
  created_at: string;
}

interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  read: boolean;
  created_at: string;
}

interface Booking {
  id: string;
  name: string;
  player_name?: string | null;
  email?: string | null;
  player_email?: string | null;
  requested_date: string | null;
  booking_date?: string | null;
  session_type: string | null;
  status: string;
  created_at: string;
}

interface TradeIn {
  id: string;
  item_name: string | null;
  status: string;
  reference_number: string | null;
  created_at: string;
}

export default function PassportPage() {
  const { profile, user, loading: authLoading, refreshProfile } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('Overview');
  const [passportEntries, setPassportEntries] = useState<PassportEntry[]>([]);
  const [activityLog, setActivityLog] = useState<ActivityEntry[]>([]);
  const [missionSubmissions, setMissionSubmissions] = useState<MissionSubmission[]>([]);
  const [coinTransactions, setCoinTransactions] = useState<CoinTransaction[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [tradeIns, setTradeIns] = useState<TradeIn[]>([]);
  const [missionCount, setMissionCount] = useState(0);
  const [redemptionCount, setRedemptionCount] = useState(0);
  const [dataLoading, setDataLoading] = useState(true);

  const loadPassportData = useCallback(async () => {
    if (!user?.id) { setDataLoading(false); return; }
    try {
      const supabase = createClient();

      const [entriesRes, activityRes, missionsRes, redemptionsRes, submissionsRes, transactionsRes, notifRes, bookingsRes, tradeRes] = await Promise.all([
        supabase.from('passport_entries').select('*').eq('user_id', user.id).order('played_at', { ascending: false }).limit(10),
        supabase.from('admin_activity_log').select('*').eq('actor_id', user.id).order('created_at', { ascending: false }).limit(8),
        supabase.from('mission_completions').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
        supabase.from('coin_redemptions').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
        supabase.from('mission_submissions').select(`*, missions:mission_id (title, difficulty, xp_reward, coin_reward, reward_coins)`).eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('coin_transactions').select('*').eq('user_id', user.id).order('created_at', { ascending: false }).limit(20),
        supabase.from('notifications').select('*').eq('user_id', user.id).order('created_at', { ascending: false }).limit(20),
        supabase.from('bookings').select('id, name, player_name, email, player_email, requested_date, booking_date, session_type, status, created_at').eq('user_id', user.id).order('created_at', { ascending: false }).limit(20),
        supabase.from('trade_in_requests').select('id, item_name, status, reference_number, created_at').eq('user_id', user.id).order('created_at', { ascending: false }).limit(10),
      ]);

      if (!entriesRes.error) setPassportEntries(entriesRes.data || []);
      if (!activityRes.error) setActivityLog(activityRes.data || []);
      if (!missionsRes.error) setMissionCount(missionsRes.count || 0);
      if (!redemptionsRes.error) setRedemptionCount(redemptionsRes.count || 0);
      if (!submissionsRes.error) {
        const subs = submissionsRes.data || [];
        const subsWithProofs = await Promise.all(subs.map(async (sub) => {
          const { data: proofs } = await supabase.from('mission_proofs').select('*').eq('submission_id', sub.id).order('proof_number');
          return { ...sub, proofs: proofs || [] };
        }));
        setMissionSubmissions(subsWithProofs);
      }
      if (!transactionsRes.error) setCoinTransactions(transactionsRes.data || []);
      if (!notifRes.error) setNotifications(notifRes.data || []);
      if (!bookingsRes.error) setBookings(bookingsRes.data || []);
      if (!tradeRes.error) setTradeIns(tradeRes.data || []);
    } catch (err) {
      console.error('Load passport data error:', err);
    } finally {
      setDataLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!authLoading) loadPassportData();
  }, [authLoading, loadPassportData]);

  // Refresh profile when tab changes to Coins to get latest balance
  useEffect(() => {
    if (activeTab === 'Coins' || activeTab === 'Overview') {
      refreshProfile();
    }
  }, [activeTab, refreshProfile]);

  const markNotifRead = async (id: string) => {
    const supabase = createClient();
    await supabase.from('notifications').update({ read: true }).eq('id', id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const markAllNotifsRead = async () => {
    const supabase = createClient();
    const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
    if (!unreadIds.length) return;
    await supabase.from('notifications').update({ read: true }).in('id', unreadIds);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  };

  const submissionStatusBadge = (s: string) => {
    if (s === 'pending_review') return <span className="badge-gold text-[9px]">⏳ PENDING REVIEW</span>;
    if (s === 'approved') return <span className="badge-green text-[9px]">✓ APPROVED</span>;
    if (s === 'rejected') return <span className="badge-red text-[9px]">✗ REJECTED</span>;
    if (s === 'needs_more_proof') return <span className="badge-blue text-[9px]">📋 NEEDS MORE PROOF</span>;
    return <span className="badge-purple text-[9px]">{s}</span>;
  };

  const bookingStatusBadge = (s: string) => {
    const map: Record<string, string> = {
      pending: 'badge-gold',
      New: 'badge-blue',
      Contacted: 'badge-purple',
      confirmed: 'badge-green',
      cancelled: 'badge-red',
      completed: 'badge-purple',
    };
    const label: Record<string, string> = {
      pending: 'Pending',
      New: 'New',
      Contacted: 'Contacted',
      confirmed: 'Confirmed',
      cancelled: 'Cancelled',
      completed: 'Completed',
    };
    return <span className={`${map[s] || 'badge-purple'} text-[9px]`}>{label[s] || s}</span>;
  };

  const displayName = profile?.username || profile?.full_name || profile?.email?.split('@')[0] || 'GXG Player';
  const initials = displayName.slice(0, 2).toUpperCase();
  const coinBalance = profile?.gxg_coins ?? 0;
  const level = profile?.level ?? 1;
  const xp = profile?.xp ?? 0;
  const tier = profile?.tier ?? 'Bronze I';
  const xpToNext = Math.max(0, (level * 1000) - xp);
  const xpProgress = Math.min(100, ((xp % 1000) / 1000) * 100);

  const unreadCount = notifications.filter(n => !n.read).length;

  if (authLoading || dataLoading) {
    return (
      <GXGLayout>
        <div className="flex items-center justify-center h-64">
          <svg className="animate-spin w-8 h-8 text-gxg-purple2" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
          <span className="ml-3 text-white/50">Loading passport...</span>
        </div>
      </GXGLayout>
    );
  }

  if (!user || !profile) {
    return (
      <GXGLayout>
        <div className="max-w-[600px] mx-auto text-center py-20">
          <div className="text-5xl mb-4">🎮</div>
          <h1 className="font-display font-black text-3xl text-white mb-3">Sign In to View Your Passport</h1>
          <p className="text-white/50 mb-6">Your GXG Passport tracks your gaming journey, missions, coins, and achievements.</p>
          <Link href="/login" className="btn-gxg-primary px-8 py-3 rounded-lg text-sm inline-block">Sign In</Link>
        </div>
      </GXGLayout>
    );
  }

  return (
    <GXGLayout>
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="mb-5">
          <h1 className="font-display font-black text-3xl md:text-4xl text-white mb-1">
            Player <span className="gxg-pink-text">Passport</span>
          </h1>
          <p className="text-white/50 text-sm">Your journey. Your legacy. Your GXG story.</p>
        </div>

        {/* Profile Card */}
        <div className="glass-card p-5 neon-border-purple relative overflow-hidden mb-5">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/15 to-blue-900/10 pointer-events-none" />
          <div className="relative z-10 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="flex-shrink-0">
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt={displayName} className="w-16 h-16 rounded-full object-cover border-2 border-purple-500/50" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-600 via-blue-600 to-pink-600 flex items-center justify-center text-xl font-bold text-white border-2 border-purple-500/50">
                  {initials}
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <h2 className="font-display font-bold text-lg text-white">{displayName}</h2>
                {profile.role !== 'public_user' && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#60a5fa"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                )}
              </div>
              <div className="text-xs text-white/50 mb-2 capitalize">{tier} · Level {level}</div>
              <div className="xp-bar max-w-xs mb-1">
                <div className="xp-bar-fill" style={{ width: `${xpProgress}%` }} />
              </div>
              <div className="text-[10px] text-white/35 font-mono">{xp.toLocaleString()} XP · {xpToNext.toLocaleString()} to Level {level + 1}</div>
            </div>
            <div className="flex flex-wrap gap-3">
              {[
                { label: 'Coins', value: coinBalance.toLocaleString(), color: 'text-gxg-gold', icon: '🪙' },
                { label: 'Missions', value: String(missionCount), color: 'text-gxg-green', icon: '🎯' },
                { label: 'Redeemed', value: String(redemptionCount), color: 'text-gxg-blue2', icon: '🎁' },
              ].map((stat, i) => (
                <div key={i} className="stat-card text-center min-w-[70px]">
                  <div className="text-base mb-0.5">{stat.icon}</div>
                  <div className={`font-display font-bold text-base ${stat.color}`}>{stat.value}</div>
                  <div className="text-[9px] text-white/40">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mb-5 border-b border-white/8 pb-3">
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`text-xs px-3 py-1.5 rounded-lg transition-all relative ${
                activeTab === tab
                  ? 'bg-purple-500/25 text-gxg-purple2 border border-purple-500/35' :'text-white/45 hover:text-white/70 hover:bg-white/5'
              }`}
            >
              {tab}
              {tab === 'Activity' && unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-gxg-pink text-white text-[9px] flex items-center justify-center font-bold">
                  {unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── OVERVIEW TAB ── */}
        {activeTab === 'Overview' && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
            <div className="xl:col-span-2 space-y-5">
              <div className="glass-card p-5">
                <h3 className="font-display font-bold text-sm text-white mb-4">Earned Badges</h3>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                  {staticBadges.map((badge, i) => (
                    <div key={i} className="flex flex-col items-center gap-1.5">
                      <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${badge.color} flex items-center justify-center text-xl`}>{badge.icon}</div>
                      <span className="text-[10px] text-white/50 text-center leading-tight">{badge.name}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="glass-card p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display font-bold text-sm text-white">Passport Entries</h3>
                  <span className="text-[10px] text-white/30">{passportEntries.length} games</span>
                </div>
                {passportEntries.length === 0 ? (
                  <div className="text-center py-6 text-white/30 text-xs">No passport entries yet. Play games at GXG to earn entries!</div>
                ) : (
                  <div className="space-y-2">
                    {passportEntries.map((entry) => (
                      <div key={entry.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/3 transition-colors">
                        <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center text-sm flex-shrink-0">🎮</div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-white/90 truncate">{entry.game_title || 'Unknown Game'}</div>
                          <div className="text-[10px] text-white/40">{formatDate(entry.played_at)}</div>
                        </div>
                        {entry.rating && (
                          <div className="flex gap-0.5">
                            {Array.from({ length: entry.rating }).map((_, j) => (
                              <span key={j} className="text-gxg-gold text-[10px]">★</span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-5">
              <div className="glass-card p-5">
                <h3 className="font-display font-bold text-base text-white mb-3">Legacy Highlights</h3>
                <div className="space-y-2">
                  {[
                    { icon: '🏆', name: 'GXG Member', desc: `Level ${level} player` },
                    { icon: '🎮', name: `${passportEntries.length} Games Played`, desc: 'Passport entries' },
                    { icon: '🎯', name: `${missionCount} Missions`, desc: 'Completed' },
                    { icon: '🪙', name: `${coinBalance.toLocaleString()} Coins`, desc: 'Current balance' },
                  ].map((h, i) => (
                    <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/3 transition-colors">
                      <span className="text-lg">{h.icon}</span>
                      <div>
                        <div className="text-xs font-semibold text-white/90">{h.name}</div>
                        <div className="text-[10px] text-white/40">{h.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── PROOF SUBMISSIONS TAB ── */}
        {activeTab === 'Proof Submissions' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-display font-bold text-lg text-white">My Proof Submissions</h2>
              <Link href="/missions" className="text-xs text-gxg-purple2 hover:text-purple-300 transition-colors">Browse Missions →</Link>
            </div>
            {missionSubmissions.length === 0 ? (
              <div className="glass-card p-10 text-center">
                <div className="text-4xl mb-3">🎯</div>
                <h3 className="font-display font-bold text-base text-white mb-2">No Submissions Yet</h3>
                <p className="text-white/40 text-sm mb-4">Complete a mission and submit proof to see it here.</p>
                <Link href="/missions" className="btn-gxg-primary px-6 py-2 rounded-lg text-sm inline-block">Browse Missions</Link>
              </div>
            ) : (
              missionSubmissions.map(sub => {
                const missionTitle = sub.missions?.title || `Mission ${sub.mission_id?.slice(0, 8) || 'Unknown'}`;
                const difficulty = sub.missions?.difficulty || '—';
                const coinReward = sub.reward_coins || sub.missions?.reward_coins || sub.missions?.coin_reward || 0;
                const xpReward = sub.xp_awarded || sub.missions?.xp_reward || 0;
                const fileProofs = sub.proofs?.filter(p => p.file_type !== 'link') || [];
                const linkProofs = sub.proofs?.filter(p => p.file_type === 'link') || [];
                const allLinks = [...(sub.proof_links || []), ...linkProofs.map(p => p.external_url || '')].filter(Boolean);

                return (
                  <div key={sub.id} className="glass-card p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-display font-bold text-sm text-white">{missionTitle}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={
                            difficulty === 'legendary' ? 'badge-gold text-[9px]' :
                            difficulty === 'hard' ? 'badge-red text-[9px]' :
                            difficulty === 'medium' ? 'badge-blue text-[9px]' : 'badge-green text-[9px]'
                          }>{difficulty ? difficulty.charAt(0).toUpperCase() + difficulty.slice(1) : difficulty}</span>
                          <span className="text-[10px] text-white/40">{formatDate(sub.created_at)}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {submissionStatusBadge(sub.status)}
                        {sub.rewards_awarded && (
                          <span className="text-[9px] text-green-400">✓ Rewards Paid</span>
                        )}
                      </div>
                    </div>

                    {/* Rewards */}
                    <div className="flex gap-4 mb-3">
                      <div className="flex items-center gap-1">
                        <CoinIcon />
                        <span className={`text-xs font-mono ${sub.coins_awarded ? 'text-green-400' : 'text-gxg-gold'}`}>
                          {coinReward} coins {sub.coins_awarded ? '✓' : ''}
                        </span>
                      </div>
                      <div className="text-xs text-gxg-blue2 font-mono">{xpReward} XP</div>
                    </div>

                    {/* Admin Notes */}
                    {sub.admin_notes && (
                      <div className="mb-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                        <div className="text-[10px] text-blue-400/70 mb-1">Admin Feedback</div>
                        <div className="text-xs text-white/70">{sub.admin_notes}</div>
                      </div>
                    )}

                    {/* Proof Files */}
                    {fileProofs.length > 0 && (
                      <div className="mb-3">
                        <div className="text-[10px] text-white/40 mb-2">{fileProofs.length} uploaded file{fileProofs.length > 1 ? 's' : ''}</div>
                        <div className="flex flex-wrap gap-2">
                          {fileProofs.map(proof => (
                            <a
                              key={proof.id}
                              href={proof.file_url || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white/5 border border-white/10 hover:border-purple-500/30 transition-colors text-[10px] text-white/60 hover:text-white/90"
                            >
                              {proof.file_type === 'video' ? '🎬' : '🖼️'}
                              <span className="truncate max-w-[100px]">{proof.file_name || `Proof #${proof.proof_number}`}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* External Links */}
                    {allLinks.length > 0 && (
                      <div className="mb-3">
                        <div className="text-[10px] text-white/40 mb-2">{allLinks.length} external link{allLinks.length > 1 ? 's' : ''}</div>
                        <div className="space-y-1">
                          {allLinks.map((link, i) => (
                            <a
                              key={i}
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1.5 text-[10px] text-gxg-blue2 hover:text-blue-300 transition-colors"
                            >
                              🔗 <span className="truncate">{link}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Resubmit button for needs_more_proof */}
                    {sub.status === 'needs_more_proof' && sub.mission_id && (
                      <div className="mt-3 pt-3 border-t border-white/8">
                        <Link
                          href={`/missions/${sub.mission_id}`}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/15 border border-blue-500/30 text-gxg-blue2 hover:bg-blue-500/25 transition-colors text-xs font-semibold"
                        >
                          📋 Submit More Proof
                        </Link>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ── MISSIONS TAB ── */}
        {activeTab === 'Missions' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-display font-bold text-lg text-white">Mission Progress</h2>
              <Link href="/missions" className="text-xs text-gxg-purple2 hover:text-purple-300 transition-colors">All Missions →</Link>
            </div>
            <div className="glass-card p-6 text-center">
              <div className="text-4xl mb-3">🎯</div>
              <div className="font-display font-bold text-2xl text-white mb-1">{missionCount}</div>
              <div className="text-white/50 text-sm mb-4">Missions Completed</div>
              <Link href="/missions" className="btn-gxg-primary px-6 py-2 rounded-lg text-sm inline-block">Browse Missions</Link>
            </div>
          </div>
        )}

        {/* ── COINS TAB ── */}
        {activeTab === 'Coins' && (
          <div className="space-y-4">
            <div className="glass-card p-5 neon-border-gold">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center text-2xl">🪙</div>
                <div>
                  <div className="font-display font-black text-3xl text-gxg-gold">{coinBalance.toLocaleString()}</div>
                  <div className="text-xs text-white/40">GXG Coins Balance</div>
                </div>
              </div>
            </div>
            <div className="glass-card overflow-hidden">
              <div className="p-4 border-b border-white/8">
                <h3 className="font-display font-bold text-sm text-white">Transaction History</h3>
              </div>
              {coinTransactions.length === 0 ? (
                <div className="text-center py-8 text-white/30 text-xs">No transactions yet.</div>
              ) : (
                <div className="divide-y divide-white/5">
                  {coinTransactions.map(tx => (
                    <div key={tx.id} className="flex items-center gap-3 px-4 py-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm flex-shrink-0 ${tx.type === 'earn' ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                        {tx.type === 'earn' ? '↑' : '↓'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-white/80 truncate">{tx.description || tx.source}</div>
                        <div className="text-[10px] text-white/40">{formatDate(tx.created_at)}</div>
                      </div>
                      <div className={`text-sm font-bold font-mono ${tx.type === 'earn' ? 'text-green-400' : 'text-red-400'}`}>
                        {tx.type === 'earn' ? '+' : '-'}{tx.amount}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── REWARDS TAB ── */}
        {activeTab === 'Rewards' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-display font-bold text-lg text-white">Rewards</h2>
              <Link href="/rewards" className="text-xs text-gxg-purple2 hover:text-purple-300 transition-colors">Redeem Rewards →</Link>
            </div>
            <div className="glass-card p-6 text-center">
              <div className="text-4xl mb-3">🎁</div>
              <div className="font-display font-bold text-2xl text-white mb-1">{redemptionCount}</div>
              <div className="text-white/50 text-sm mb-4">Rewards Redeemed</div>
              <Link href="/rewards" className="btn-gxg-primary px-6 py-2 rounded-lg text-sm inline-block">Browse Rewards</Link>
            </div>
          </div>
        )}

        {/* ── BOOKINGS TAB ── */}
        {activeTab === 'Bookings' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-display font-bold text-lg text-white">My Bookings</h2>
              <Link href="/private-gaming" className="text-xs text-gxg-purple2 hover:text-purple-300 transition-colors">Book a Session →</Link>
            </div>
            {bookings.length === 0 ? (
              <div className="glass-card p-10 text-center">
                <div className="text-4xl mb-3">📅</div>
                <h3 className="font-display font-bold text-base text-white mb-2">No Bookings Yet</h3>
                <p className="text-white/40 text-sm mb-4">Book a private gaming session to see it here.</p>
                <Link href="/private-gaming" className="btn-gxg-primary px-6 py-2 rounded-lg text-sm inline-block">Book a Session</Link>
              </div>
            ) : (
              <div className="glass-card overflow-hidden">
                <div className="divide-y divide-white/5">
                  {bookings.map(b => {
                    const displayDate = b.requested_date || b.booking_date;
                    const displayName2 = b.player_name || b.name || '—';
                    return (
                      <div key={b.id} className="flex items-center gap-4 px-4 py-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center text-lg flex-shrink-0">📅</div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-white/90">{b.session_type || 'Session'}</div>
                          <div className="text-[10px] text-white/40">
                            {displayDate ? new Date(displayDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Date TBD'}
                          </div>
                        </div>
                        <div>{bookingStatusBadge(b.status)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── TRADE REQUESTS TAB ── */}
        {activeTab === 'Trade Requests' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-display font-bold text-lg text-white">My Trade Requests</h2>
              <Link href="/sell-trade" className="text-xs text-gxg-purple2 hover:text-purple-300 transition-colors">Submit Trade →</Link>
            </div>
            {tradeIns.length === 0 ? (
              <div className="glass-card p-10 text-center">
                <div className="text-4xl mb-3">🔄</div>
                <h3 className="font-display font-bold text-base text-white mb-2">No Trade Requests Yet</h3>
                <p className="text-white/40 text-sm mb-4">Submit a trade-in request to see it here.</p>
                <Link href="/sell-trade" className="btn-gxg-primary px-6 py-2 rounded-lg text-sm inline-block">Sell or Trade</Link>
              </div>
            ) : (
              <div className="glass-card overflow-hidden">
                <div className="divide-y divide-white/5">
                  {tradeIns.map(t => (
                    <div key={t.id} className="flex items-center gap-4 px-4 py-3">
                      <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center text-lg flex-shrink-0">🔄</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-white/90">{t.item_name || 'Trade Request'}</div>
                        <div className="text-[10px] text-white/40">
                          {t.reference_number && <span className="font-mono text-gxg-purple2 mr-2">{t.reference_number}</span>}
                          {formatDate(t.created_at)}
                        </div>
                      </div>
                      <span className={`text-[9px] px-2 py-0.5 rounded-full border ${
                        t.status === 'accepted' ? 'border-green-500/30 text-green-400' :
                        t.status === 'rejected' ? 'border-red-500/30 text-red-400' :
                        t.status === 'completed'? 'border-purple-500/30 text-gxg-purple2' : 'border-yellow-500/30 text-yellow-400'
                      } capitalize`}>{t.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── GAME REQUESTS TAB ── */}
        {activeTab === 'Game Requests' && (
          <div className="glass-card p-10 text-center">
            <div className="text-4xl mb-3">🎮</div>
            <h3 className="font-display font-bold text-base text-white mb-2">Game Requests</h3>
            <p className="text-white/40 text-sm">Game request tracking coming soon.</p>
          </div>
        )}

        {/* ── ACTIVITY TAB ── */}
        {activeTab === 'Activity' && (
          <div className="space-y-4">
            {unreadCount > 0 && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                <span className="text-xs text-gxg-purple2">{unreadCount} unread notification{unreadCount > 1 ? 's' : ''}</span>
                <button onClick={markAllNotifsRead} className="text-[10px] text-white/50 hover:text-white/80 transition-colors">Mark all read</button>
              </div>
            )}
            {notifications.length > 0 && (
              <div className="glass-card overflow-hidden">
                <div className="p-4 border-b border-white/8">
                  <h3 className="font-display font-bold text-sm text-white">Notifications</h3>
                </div>
                <div className="divide-y divide-white/5">
                  {notifications.map(n => (
                    <div
                      key={n.id}
                      onClick={() => markNotifRead(n.id)}
                      className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-white/3 transition-colors ${!n.read ? 'bg-purple-500/5' : ''}`}
                    >
                      <span className="text-sm flex-shrink-0 mt-0.5">
                        {n.type === 'success' ? '✅' : n.type === 'error' ? '❌' : n.type === 'warning' ? '⚠️' : '🔔'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className={`text-xs font-semibold ${!n.read ? 'text-white' : 'text-white/70'}`}>{n.title}</div>
                        <div className="text-[10px] text-white/40 mt-0.5">{n.message}</div>
                        <div className="text-[10px] text-white/25 mt-1">{formatDate(n.created_at)}</div>
                      </div>
                      {!n.read && <div className="w-2 h-2 rounded-full bg-gxg-purple2 flex-shrink-0 mt-1.5" />}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {activityLog.length > 0 && (
              <div className="glass-card overflow-hidden">
                <div className="p-4 border-b border-white/8">
                  <h3 className="font-display font-bold text-sm text-white">Recent Activity</h3>
                </div>
                <div className="divide-y divide-white/5">
                  {activityLog.map(entry => (
                    <div key={entry.id} className="flex items-center gap-3 px-4 py-3">
                      <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-sm flex-shrink-0">📋</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-white/70 capitalize">{entry.action.replace(/_/g, ' ')}</div>
                        <div className="text-[10px] text-white/40">{formatDate(entry.created_at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {notifications.length === 0 && activityLog.length === 0 && (
              <div className="glass-card p-10 text-center">
                <div className="text-4xl mb-3">📋</div>
                <h3 className="font-display font-bold text-base text-white mb-2">No Activity Yet</h3>
                <p className="text-white/40 text-sm">Your activity and notifications will appear here.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </GXGLayout>
  );
}
